











































import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/InlandLogistics.css";
import axios from "axios";
// Import your icon and image
import logisticsIcon from "../assets/icons/logistics-icon.png";
import logisticsImage from "../image/InlandLogistics-image.png";

const InlandLogistics = () => {
  const navigate = useNavigate();
  const [equipmentData, setEquipmentData] = useState({
    cranes: [],
    forklifts: [],
    trailers: []
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hoveredCell, setHoveredCell] = useState({ row: null, col: null, category: null });
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tableRef = useRef(null);
  
  useEffect(() => {
    fetchEquipmentData();
    fetchCategories();
    
    // Check if user is logged in
    const adminStatus = sessionStorage.getItem('isAdmin');
    if (adminStatus === 'true') {
      setIsAdmin(true);
    }
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('https://ecl.net.in/backende/api/equipment-categories');
      setCategories(response.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchEquipmentData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://ecl.net.in/backende/api/equipment');
      
      // Check if response is successful and contains equipment data
      if (response.data && (response.data.equipment || Array.isArray(response.data))) {
        // Handle both response formats - either response.data.equipment or direct array
        const equipmentList = response.data.equipment || response.data;
        
        // Ensure equipmentList is an array
        if (!Array.isArray(equipmentList)) {
          throw new Error("Equipment data is not in array format");
        }
        
        // Organize data by category
        const organized = {
          cranes: [],
          forklifts: [],
          trailers: []
        };
        
        // Process equipment data
        equipmentList.forEach(item => {
          // Extract category name, making it lowercase for consistency
          const categoryName = (item.category_name || "").toLowerCase();
          
          // Map the backend fields to the expected frontend structure
          const processedItem = {
            id: item.id,
            name: item.name || "",
            category: categoryName,
            // Add all attribute values directly to the item
            ...(item.attribute_values || []).reduce((acc, attr) => {
              if (attr.attribute_name) {
                acc[attr.attribute_name] = attr.value;
              }
              return acc;
            }, {}),
            // Still specifically look for these common attributes
            capacity: findAttributeValue(item, "capacity") || item.measurement || "",
            size: findAttributeValue(item, "size") || item.measurement || "",
            location: findAttributeValue(item, "location") || "",
            description: item.description || "",
            // Keep the original attribute_values array for reference
            attribute_values: item.attribute_values || []
          };
          
          // Add to the appropriate category
          if (categoryName === "cranes" && !organized.cranes.includes(processedItem)) {
            organized.cranes.push(processedItem);
          } else if (categoryName === "forklifts" && !organized.forklifts.includes(processedItem)) {
            organized.forklifts.push(processedItem);
          } else if (categoryName === "trailers" && !organized.trailers.includes(processedItem)) {
            organized.trailers.push(processedItem);
          } else if (organized[categoryName]) {
            // Handle any other category that might be in the organized object
            organized[categoryName].push(processedItem);
          } else {
            // Create a new category if it doesn't exist
            organized[categoryName] = [processedItem];
          }
        });
        
        setEquipmentData(organized);
        setLoading(false);
        setError(null);
      } else {
        throw new Error("Invalid response format from API");
      }
    } catch (err) {
      console.error("Error fetching equipment data:", err);
      setError("Failed to load equipment data. " + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };
  
  // Helper function to find attribute values from the item's attribute_values array
  const findAttributeValue = (item, attributeName) => {
    if (!item.attribute_values || !Array.isArray(item.attribute_values)) {
      return item[attributeName] || null; // Try direct property access first
    }
    
    // Look for an exact match on attribute name
    let attribute = item.attribute_values.find(
      attr => attr.attribute_name && attr.attribute_name.toLowerCase() === attributeName.toLowerCase()
    );
    
    // If not found by exact name, try to find by attribute_display_name
    if (!attribute) {
      attribute = item.attribute_values.find(
        attr => attr.attribute_display_name && 
        attr.attribute_display_name.toLowerCase() === attributeName.toLowerCase()
      );
    }
    
    // If still not found, try partial matching in display_name
    if (!attribute) {
      attribute = item.attribute_values.find(
        attr => attr.attribute_display_name && 
        attr.attribute_display_name.toLowerCase().includes(attributeName.toLowerCase())
      );
    }
    
    // If still not found, look for any similar keywords
    if (!attribute) {
      attribute = item.attribute_values.find(
        attr => attr.attribute_display_name && 
        [attributeName, 'measurement', 'capacity', 'size'].some(term => 
          attr.attribute_display_name.toLowerCase().includes(term.toLowerCase())
        )
      );
    }
    
    // If still not found and this is capacity/size, use the first numeric attribute value
    if (!attribute && ['capacity', 'size'].includes(attributeName.toLowerCase())) {
      attribute = item.attribute_values.find(
        attr => !isNaN(parseFloat(attr.value))
      );
    }
    
    return attribute ? attribute.value : (item[attributeName] || null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('isAdmin');
    setIsAdmin(false);
    navigate('/login');
  };

  const goToAdminDashboard = () => {
    navigate('/dashbord');
  };

  // Handle cell hover to show row and column names
  const handleCellHover = (e, rowIndex, colIndex, category) => {
    // Get mouse position relative to viewport
    const x = e.clientX;
    const y = e.clientY;
    
    setHoveredCell({ row: rowIndex, col: colIndex, category });
    setTooltipPosition({ x, y });
  };

  const handleCellLeave = () => {
    setHoveredCell({ row: null, col: null, category: null });
  };

  // Get the appropriate cell content based on category and column index
  const getCellContent = (item, columnIndex, category) => {
    if (!item) return "";
    
    if (columnIndex === 0) return item.name || "";
    
    // For the second column (index 1)
    // Get category configuration from categories state
    const categoryConfig = categories.find(cat => 
      cat.name && cat.name.toLowerCase() === category.toLowerCase()
    );
    
    // If we have category attributes defined by the admin
    if (categoryConfig && categoryConfig.attributes && categoryConfig.attributes.length > 0) {
      // Get all attributes for this category
      const categoryAttributes = categoryConfig.attributes;
      
      // Try to find attribute values for this item
      if (item.attribute_values && Array.isArray(item.attribute_values)) {
        // For each attribute in the category (prioritizing the first one)
        for (const categoryAttr of categoryAttributes) {
          // Find matching attribute value in the item
          const matchingAttr = item.attribute_values.find(itemAttr => 
            itemAttr.attribute_id === categoryAttr.id || 
            (itemAttr.attribute_name && itemAttr.attribute_name === categoryAttr.name)
          );
          
          if (matchingAttr && matchingAttr.value) {
            // Add MT if needed for capacity in cranes/forklifts
            if ((category.toLowerCase() === 'cranes' || category.toLowerCase() === 'forklifts') &&
                !matchingAttr.value.toString().toLowerCase().includes('mt') &&
                categoryAttr.display_name.toLowerCase().includes('capacity')) {
              return `${matchingAttr.value} MT`;
            }
            return matchingAttr.value;
          }
        }
        
        // If no match found with category attributes, use the first attribute value
        if (item.attribute_values.length > 0) {
          const firstAttr = item.attribute_values[0];
          return firstAttr.value || "";
        }
      }
      
      // Try direct property access based on attribute name
      for (const categoryAttr of categoryAttributes) {
        if (item[categoryAttr.name]) {
          return item[categoryAttr.name];
        }
      }
    }
    
    // Fallback to category-specific defaults
    switch(category.toLowerCase()) {
      case 'cranes':
      case 'forklifts':
        if (item.capacity) {
          return item.capacity.toString().toLowerCase().includes('mt') ? 
            item.capacity : `${item.capacity} MT`;
        }
        break;
      case 'trailers':
        if (item.size) return item.size;
        break;
      default:
        // Try various measurement properties
        if (item.measurement) return item.measurement;
        if (item.capacity) return item.capacity;
        
        // If all else fails, look for any numeric attribute value
        if (item.attribute_values && Array.isArray(item.attribute_values) && item.attribute_values.length > 0) {
          // Pick the first numeric value found
          const numericAttr = item.attribute_values.find(
            attr => !isNaN(parseFloat(attr.value))
          );
          if (numericAttr) return numericAttr.value;
          
          // Otherwise just return the first attribute value
          return item.attribute_values[0].value || "";
        }
    }
    
    return "";
  };

  // Get the column header based on category and column index
  const getColumnHeader = (columnIndex, category) => {
    if (columnIndex === 0) return `Name`;
    
    // For column index 1 (the measurement/attribute column)
    // Try to get headers from category configuration
    const categoryConfig = categories.find(cat => 
      cat.name && cat.name.toLowerCase() === category.toLowerCase()
    );
    
    // If we have attributes defined by admin for this category
    if (categoryConfig && categoryConfig.attributes && categoryConfig.attributes.length > 0) {
      // Use the first attribute's display name (this is what admin configured)
      const firstAttribute = categoryConfig.attributes[0];
      
      // If the admin has set a display name, use it
      if (firstAttribute.display_name) {
        return firstAttribute.display_name;
      }
      
      // Otherwise use the attribute name
      if (firstAttribute.name) {
        return firstAttribute.name;
      }
    }
    
    // Fallback to category-specific defaults
    switch(category.toLowerCase()) {
      case 'cranes':
      case 'forklifts':
        return "Capacity (MT)";
      case 'trailers':
        return "Size";
      default:
        // Try to get header from equipment data if available
        const equipment = Object.values(equipmentData)
          .flat()
          .find(item => item && item.category === category);
          
        if (equipment && equipment.attribute_values && equipment.attribute_values.length > 0) {
          const attr = equipment.attribute_values[0];
          return attr.attribute_display_name || attr.attribute_name || "Measurement";
        }
        
        return "Measurement";
    }
  };

  // Get category display name
  const getCategoryDisplayName = (category) => {
    const categoryConfig = categories.find(cat => cat.name.toLowerCase() === category.toLowerCase());
    return categoryConfig ? categoryConfig.name : category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Check if a row should be highlighted
  const isRowHighlighted = (rowIndex) => {
    return hoveredCell.row === rowIndex;
  };

  // Check if a column should be highlighted
  const isColumnHighlighted = (colIndex, category) => {
    return hoveredCell.col === colIndex && hoveredCell.category === category;
  };

  // Render tooltip for hovered cell
  const renderTooltip = () => {
    if (hoveredCell.row === null && hoveredCell.col === null) return null;

    // Get the hovered category
    const category = hoveredCell.category;
    if (!category || !equipmentData[category]) return null;

    // Get the row data (equipment item)
    const rowData = hoveredCell.row !== null && hoveredCell.row < equipmentData[category].length
      ? equipmentData[category][hoveredCell.row]
      : null;

    // Get column header
    const columnHeader = hoveredCell.col !== null 
      ? getColumnHeader(hoveredCell.col, category)
      : null;

    // Calculate tooltip position to ensure it stays in viewport
    // Adjust position to avoid being cut off at screen edges
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 250; // Approximate width of tooltip
    const tooltipHeight = 150; // Approximate height of tooltip
    
    let x = tooltipPosition.x + 20; // Add some offset from cursor
    let y = tooltipPosition.y + 20;
    
    // Adjust if tooltip would go off the right side
    if (x + tooltipWidth > viewportWidth) {
      x = tooltipPosition.x - tooltipWidth - 10;
    }
    
    // Adjust if tooltip would go off the bottom
    if (y + tooltipHeight > viewportHeight) {
      y = tooltipPosition.y - tooltipHeight - 10;
    }

    const tooltipStyle = {
      top: y + 'px',
      left: x + 'px',
      position: 'fixed', // Use fixed positioning relative to viewport
      zIndex: 9999
    };

    return (
      <div className="equipment-tooltip" style={tooltipStyle}>
        {rowData && columnHeader && (
          <>
            <div className="tooltip-header">
              <span className="tooltip-category">{getCategoryDisplayName(category)}</span>
            </div>
            <div className="tooltip-content">
              {hoveredCell.col === 0 ? (
                <div className="tooltip-row">
                  <span className="tooltip-label">Name:</span>
                  <span className="tooltip-value">{rowData.name}</span>
                </div>
              ) : (
                <div className="tooltip-row">
                  <span className="tooltip-label">{columnHeader}:</span>
                  <span className="tooltip-value">
                    {getCellContent(rowData, hoveredCell.col, category)}
                  </span>
                </div>
              )}
              {rowData.location && (
                <div className="tooltip-row">
                  <span className="tooltip-label">Location:</span>
                  <span className="tooltip-value">{rowData.location}</span>
                </div>
              )}
              {rowData.description && (
                <div className="tooltip-description">
                  {rowData.description}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="logistics-container">
      {/* Image Slider */}
      <div className="logistics-image-slider">
        <img src={logisticsImage} alt="Inland Logistics" />
      </div>
      {/* Main Content Container */}
      <div className="logistics-content">
        {/* Content Card with title inside */}
        <div className="logistics-card">
          {/* Ship wheel icon and title */}
          <div className="logistics-header-container">
            <img src={logisticsIcon} alt="Logistics" className="logistics-wheel-icon" />
            <div className="logistics-header-title">Inland Logistics</div>
          </div>
          
          {/* Content */}
          <div className="logistics-text-content">
            <p className="logistics-text">
              Concurrent with their Liner service, ECL Shipping India Pvt Ltd offers Inland Logistics solutions as a value added service to complement their ocean transportation service.
            </p>
            
            <h3 className="logistics-subtitle">ECL Shipping India Pvt Ltd's Inland transportation services cover</h3>
            
            <ol className="logistics-numbered-list">
              <li>Timely pickup and delivery</li>
              <li>Cost efficient and One point of contact</li>
              <li>Safe handling of Cargo</li>
              <li>Constant monitoring and quality assurance</li>
              <li>Customs clearance at Port / CFS</li>
              <li>Cargo survey</li>
              <li>Warehousing / Stockyards</li>
             
            </ol>
            
            <p className="logistics-text">
              ECL Shipping India Pvt Ltds fleet of trailers, cranes and forklifts, enable it to provide a total and comprehensive logistical solution to the trade.
            </p>
          </div>
          
          {/* Right Side Panel */}
          <div className="logistics-side-panel">
            <Link to="/map" className="logistics-panel-link">
              <div className="logistics-panel-section">
                <h4 className="logistics-panel-title">Map :</h4>
                <p className="logistics-panel-text">
                  Get the Map & Routes
                </p>
              </div>
            </Link>
            
            <Link to="/cargoes" className="logistics-panel-link">
              <div className="logistics-panel-section">
                <h4 className="logistics-panel-title">Cargoes :</h4>
                <p className="logistics-panel-text">
                  Break bulk cargoes are classified as cargoes which are packed in cases, bales, cartons, drums etc., and these cargo are carried in the ship's cargo holds rather than in containers.
                </p>
              </div>
            </Link>
          </div>
        </div>
        
        {/* Equipment Infrastructure Card */}
        {/* <div className="logistics-card equipment-card">
          <div className="logistics-header-container">
            <img src={logisticsIcon} alt="Equipment" className="logistics-wheel-icon" />
            <div className="logistics-header-title">Equipment's Infrastructure</div>

            {isAdmin && (
              <div className="admin-actions">
                <span className="admin-welcome">Welcome, Admin</span>
                <button 
                  className="go-to-dashboard-btn"
                  onClick={goToAdminDashboard}
                >
                  Admin Dashboard
                </button>
                <button 
                  className="edit-equipment-btn"
                  onClick={() => navigate('/admin/equipment')}
                >
                  Edit Equipment
                </button>
                <button 
                  className="logout-btn"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
          
          <div className="equipment-content">
            {loading ? (
              <div className="equipment-loading">
                <div className="equipment-spinner"></div>
                <span>Loading equipment data...</span>
              </div>
            ) : error ? (
              <div className="equipment-error">{error}</div>
            ) : (
              <div className="equipment-table-container" ref={tableRef}>
              
                {renderTooltip()}
                
                <table className="equipment-table">
                  <thead>
                    <tr>
                      {Object.keys(equipmentData).map(category => (
                        equipmentData[category] && equipmentData[category].length > 0 ? (
                          <th 
                            key={`header-${category}`} 
                            colSpan="2" 
                            className="equipment-category-header"
                          >
                            {getCategoryDisplayName(category)}
                          </th>
                        ) : null
                      ))}
                    </tr>
                    <tr>
                      {Object.keys(equipmentData).map(category => (
                        equipmentData[category] && equipmentData[category].length > 0 ? (
                          [0, 1].map(colIndex => (
                            <th 
                              key={`subheader-${category}-${colIndex}`} 
                              className={`equipment-subheader ${isColumnHighlighted(colIndex, category) ? 'highlight-column' : ''}`}
                            >
                              {getColumnHeader(colIndex, category)}
                            </th>
                          ))
                        ) : null
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ 
                      length: Math.max(
                        ...Object.values(equipmentData)
                          .filter(Array.isArray)
                          .map(arr => arr.length || 0),
                        0 
                      ) 
                    }).map((_, rowIndex) => (
                      <tr 
                        key={`row-${rowIndex}`}
                        className={isRowHighlighted(rowIndex) ? 'highlight-row' : ''}
                      >
                        {Object.keys(equipmentData).map(category => (
                          equipmentData[category] && equipmentData[category].length > 0 ? (
                            [0, 1].map(colIndex => {
                              const isEmpty = !equipmentData[category] || rowIndex >= equipmentData[category].length;
                              const item = !isEmpty ? equipmentData[category][rowIndex] : null;
                              const cellContent = !isEmpty ? getCellContent(item, colIndex, category) : "";
                              const isHighlighted = isColumnHighlighted(colIndex, category);
                              
                              return (
                                <td 
                                  key={`cell-${category}-${rowIndex}-${colIndex}`}
                                  className={`${isEmpty ? 'empty-cell' : ''} ${isHighlighted ? 'highlight-column' : ''}`}
                                  onMouseEnter={(e) => handleCellHover(e, rowIndex, colIndex, category)}
                                  onMouseLeave={handleCellLeave}
                                  data-row={rowIndex}
                                  data-col={colIndex}
                                  data-category={category}
                                >
                                  {cellContent}
                                </td>
                              );
                            })
                          ) : null
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default InlandLogistics;